// src/pages/profile/ChangePassword.tsx
import React, { useState } from 'react';
import { Lock, Eye, EyeOff, Save, Shield, CheckCircle } from 'lucide-react';
import { Button, Card, Breadcrumb, Input } from '../../components/ui';
import { authService } from '../../services/auth';
import toast from 'react-hot-toast';

const ChangePassword: React.FC = () => {
  const [saving, setSaving] = useState(false);
  const [showOld, setShowOld] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [formData, setFormData] = useState({
    old_password: '',
    new_password: '',
    confirm_password: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const passwordStrength = (password: string) => {
    let score = 0;
    if (password.length >= 8) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[a-z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[^A-Za-z0-9]/.test(password)) score++;
    return score;
  };

  const strength = passwordStrength(formData.new_password);
  const strengthLabel = ['', 'Sangat Lemah', 'Lemah', 'Cukup', 'Kuat', 'Sangat Kuat'][strength];
  const strengthColor = ['', 'bg-danger', 'bg-orange-400', 'bg-warning', 'bg-teal-light', 'bg-success'][strength];

  const handleSubmit = async () => {
    const newErrors: Record<string, string> = {};
    if (!formData.old_password) newErrors.old_password = 'Password lama wajib diisi';
    if (!formData.new_password) newErrors.new_password = 'Password baru wajib diisi';
    if (formData.new_password.length < 8) newErrors.new_password = 'Minimal 8 karakter';
    if (formData.new_password !== formData.confirm_password) newErrors.confirm_password = 'Password tidak cocok';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setSaving(true);
    try {
      await authService.changePassword({
        current_password: formData.old_password,
        password: formData.new_password,
        password_confirmation: formData.confirm_password,
      });
      toast.success('Password berhasil diubah');
      setFormData({ old_password: '', new_password: '', confirm_password: '' });
      setErrors({});
    } catch (e: any) {
      if (e.response?.status === 422) {
        // Validation errors from backend
        const backendErrors = e.response.data.errors;
        const mappedErrors: Record<string, string> = {};
        if (backendErrors.current_password) mappedErrors.old_password = backendErrors.current_password[0];
        if (backendErrors.password) mappedErrors.new_password = backendErrors.password[0];
        setErrors(mappedErrors);
        toast.error('Gagal mengubah password. Periksa input anda.');
      } else {
        toast.error('Terjadi kesalahan saat mengubah password');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 animate-in">
      <Breadcrumb items={[
        { label: 'Profil', href: '/profile' },
        { label: 'Ubah Password' },
      ]} />

      <h1 className="text-2xl font-black text-teal-dark tracking-tight">Ubah Password</h1>

      <div className="max-w-lg">
        <Card>
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-teal-main/10 rounded-xl">
              <Shield className="w-6 h-6 text-teal-main" />
            </div>
            <div>
              <h3 className="text-base font-bold text-neutral-text-primary">Keamanan Akun</h3>
              <p className="text-xs text-neutral-text-secondary">Pastikan password kuat dan unik</p>
            </div>
          </div>

          <div className="space-y-4">
            {/* Old Password */}
            <div className="relative">
              <Input
                label="Password Lama"
                type={showOld ? 'text' : 'password'}
                value={formData.old_password}
                onChange={(e) => { setFormData({ ...formData, old_password: e.target.value }); setErrors({}); }}
                error={errors.old_password}
                icon={<Lock className="w-4 h-4" />}
                required
              />
              <button
                type="button"
                onClick={() => setShowOld(!showOld)}
                className="absolute right-3 top-9 text-neutral-text-secondary hover:text-neutral-text-primary"
              >
                {showOld ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>

            {/* New Password */}
            <div className="relative">
              <Input
                label="Password Baru"
                type={showNew ? 'text' : 'password'}
                value={formData.new_password}
                onChange={(e) => { setFormData({ ...formData, new_password: e.target.value }); setErrors({}); }}
                error={errors.new_password}
                icon={<Lock className="w-4 h-4" />}
                required
              />
              <button
                type="button"
                onClick={() => setShowNew(!showNew)}
                className="absolute right-3 top-9 text-neutral-text-secondary hover:text-neutral-text-primary"
              >
                {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>

            {/* Strength Indicator */}
            {formData.new_password && (
              <div className="space-y-2">
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <div key={i} className={`h-1.5 flex-1 rounded-full ${i <= strength ? strengthColor : 'bg-gray-200'}`} />
                  ))}
                </div>
                <p className={`text-xs font-semibold ${strength >= 4 ? 'text-success' : strength >= 3 ? 'text-warning' : 'text-danger'}`}>
                  Kekuatan: {strengthLabel}
                </p>
                <div className="space-y-1 text-xs text-neutral-text-secondary">
                  {[
                    { check: formData.new_password.length >= 8, label: 'Minimal 8 karakter' },
                    { check: /[A-Z]/.test(formData.new_password), label: 'Mengandung huruf besar' },
                    { check: /[a-z]/.test(formData.new_password), label: 'Mengandung huruf kecil' },
                    { check: /[0-9]/.test(formData.new_password), label: 'Mengandung angka' },
                    { check: /[^A-Za-z0-9]/.test(formData.new_password), label: 'Mengandung karakter spesial' },
                  ].map((rule, i) => (
                    <div key={i} className={`flex items-center gap-1.5 ${rule.check ? 'text-success' : ''}`}>
                      <CheckCircle className={`w-3 h-3 ${rule.check ? 'text-success' : 'text-gray-300'}`} />
                      {rule.label}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Confirm Password */}
            <div className="relative">
              <Input
                label="Konfirmasi Password Baru"
                type={showConfirm ? 'text' : 'password'}
                value={formData.confirm_password}
                onChange={(e) => { setFormData({ ...formData, confirm_password: e.target.value }); setErrors({}); }}
                error={errors.confirm_password}
                icon={<Lock className="w-4 h-4" />}
                required
              />
              <button
                type="button"
                onClick={() => setShowConfirm(!showConfirm)}
                className="absolute right-3 top-9 text-neutral-text-secondary hover:text-neutral-text-primary"
              >
                {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>

            <div className="pt-4">
              <Button variant="primary" className="w-full" onClick={handleSubmit} loading={saving} icon={<Save className="w-4 h-4" />}>
                Ubah Password
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default ChangePassword;